import axios from 'axios';
import { baseUrl } from '.';
import { stripStoredUserTokenSuffix } from './token';
import {
  SavedSearch,
  SavedSearchInput,
  SavedSearchListResult,
  SavedSearchWriteResult,
  SavedSearchMatch,
  SavedSearchMatchesResult,
} from '../types/savedSearch';

// Buyer saved-search API layer. All calls are buyer-token scoped and fail
// closed (empty results / ok: false) — never a page reload on error, unlike
// the legacy getProperties handler.

const authHeaders = (token: string) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${stripStoredUserTokenSuffix(token)}`,
});

export const listSavedSearches = async (
  token: string
): Promise<SavedSearchListResult> => {
  try {
    if (!token) return { ok: false, data: [] };
    const response = await axios.get(`${baseUrl}/whitelabel/saved-searches`, {
      headers: authHeaders(token),
    });
    const rows = response.data?.data;
    return { ok: true, data: Array.isArray(rows) ? (rows as SavedSearch[]) : [] };
  } catch {
    return { ok: false, data: [] };
  }
};

const writeResultFromError = (error: any): SavedSearchWriteResult => ({
  ok: false,
  status: error?.response?.status ?? 500,
  message: error?.response?.data?.message ?? 'Something went wrong',
  code: error?.response?.data?.code,
});

export const createSavedSearch = async (
  token: string,
  input: SavedSearchInput
): Promise<SavedSearchWriteResult> => {
  try {
    if (!token) return { ok: false, status: 401, message: 'Authentication required' };
    const response = await axios.post(
      `${baseUrl}/whitelabel/saved-searches`,
      input,
      { headers: authHeaders(token) }
    );
    return { ok: true, status: response.status, data: response.data?.data };
  } catch (error) {
    return writeResultFromError(error);
  }
};

export const updateSavedSearch = async (
  token: string,
  searchId: string,
  input: SavedSearchInput
): Promise<SavedSearchWriteResult> => {
  try {
    if (!token) return { ok: false, status: 401, message: 'Authentication required' };
    const response = await axios.put(
      `${baseUrl}/whitelabel/saved-searches/${searchId}`,
      input,
      { headers: authHeaders(token) }
    );
    return { ok: true, status: response.status, data: response.data?.data };
  } catch (error) {
    return writeResultFromError(error);
  }
};

export const deleteSavedSearch = async (
  token: string,
  searchId: string
): Promise<SavedSearchWriteResult> => {
  try {
    if (!token) return { ok: false, status: 401, message: 'Authentication required' };
    const response = await axios.delete(
      `${baseUrl}/whitelabel/saved-searches/${searchId}`,
      { headers: authHeaders(token) }
    );
    return { ok: true, status: response.status };
  } catch (error) {
    return writeResultFromError(error);
  }
};

export const getSavedSearchMatches = async (
  token: string,
  page = 1,
  limit = 20
): Promise<SavedSearchMatchesResult> => {
  try {
    if (!token) return { ok: false, matches: [], total: 0, unseenCount: 0 };
    const response = await axios.get(
      `${baseUrl}/whitelabel/saved-search-matches`,
      { headers: authHeaders(token), params: { page, limit } }
    );
    const body = response.data ?? {};
    return {
      ok: true,
      matches: Array.isArray(body.data) ? (body.data as SavedSearchMatch[]) : [],
      total: typeof body.total === 'number' ? body.total : 0,
      unseenCount: typeof body.unseenCount === 'number' ? body.unseenCount : 0,
    };
  } catch {
    return { ok: false, matches: [], total: 0, unseenCount: 0 };
  }
};

export const getUnseenMatchCount = async (token: string): Promise<number> => {
  try {
    if (!token) return 0;
    const response = await axios.get(
      `${baseUrl}/whitelabel/saved-search-matches/unseen-count`,
      { headers: authHeaders(token) }
    );
    const count = response.data?.data?.count;
    return typeof count === 'number' ? count : 0;
  } catch {
    return 0;
  }
};

export const markMatchesSeen = async (token: string): Promise<boolean> => {
  try {
    if (!token) return false;
    await axios.post(
      `${baseUrl}/whitelabel/saved-search-matches/seen`,
      {},
      { headers: authHeaders(token) }
    );
    return true;
  } catch {
    return false;
  }
};

// The saved-search form's location/type/strategy options come from the same
// facets the listing page uses. Site-token scoped (catalogue endpoint) and
// fails closed to null so the form degrades to free-progress without options.
export interface SearchFacets {
  regions: string[];
  towns: string[];
  propertyType: string[];
  strategy: string[];
}

export const fetchSearchFacets = async (): Promise<SearchFacets | null> => {
  try {
    const siteToken = (window as any).__TENANT__?.token;
    if (!siteToken) return null;
    const response = await axios.post(
      `${baseUrl}/whitelabel/properties`,
      { page: 1, limit: 1 },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${siteToken}`,
        },
      }
    );
    const filter = response.data?.filter;
    if (!filter) return null;
    return {
      regions: Array.isArray(filter.regions) ? filter.regions : [],
      towns: Array.isArray(filter.towns) ? filter.towns : [],
      propertyType: Array.isArray(filter.propertyType) ? filter.propertyType : [],
      strategy: Array.isArray(filter.strategy) ? filter.strategy : [],
    };
  } catch {
    return null;
  }
};
